/**
 * Per-model pages for the equipment catalog other than recorders (those keep
 * `/nvr/:slug`, see nvrSlug.ts): PoE switches, UPS, power supplies, mounts and
 * junction boxes, alarm panels and detectors, access control, intercoms.
 *
 * Same rule as the recorder and camera pages: only an entry marked
 * `verified: true` with a `datasheetUrl` gets a public page, so every figure
 * on it traces back to the maker's own page or datasheet. Mounts all carry
 * their source (MOUNT_ACCESSORIES requires datasheetUrl).
 *
 * Address: `/equipment/<category>/<manufacturer-model>`; hubs at `/equipment`
 * and `/equipment/<category>`.
 */
import { EQUIPMENT_CATALOG, type EquipmentCatalogEntry } from '@/components/cctv-designer-v2/equipmentCatalog';
import { MOUNT_ACCESSORIES, type MountAccessory, type MountKind } from '@/data/mountAccessories';
// Dahua's mounts read from its camera pages carry no drawing geometry, so the planner's list keeps only the measured
// ones (mountAccessories.ts); their pages still come from here.
import { DAHUA_MOUNT_ACCESSORIES } from '@/data/mountAccessories-dahua.generated';
import compatibility from '../../tools/accessory-compatibility.json';
import { getAllCamerasForStaticPages } from './cameraSlug';

export type EquipmentPageCategory = EquipmentCatalogEntry['category'] | 'mount';

export interface EquipmentPageData {
  category: EquipmentPageCategory;
  manufacturer: string;
  model: string;
  slug: string;
  /** "/equipment/poe-switch/tp-link-tl-sg1210p" */
  path: string;
  tagline?: string;
  datasheetUrl: string;
  /** The entry's figures, flat (a mount's own fields; a catalog entry's specs). */
  specs: Record<string, unknown>;
  /** Mounts only: the bracket's kind. */
  mountKind?: MountKind;
}

/** Singular label, plural label, schema.org-ish category words. Order = hub order. */
export const EQUIPMENT_CATEGORIES: ReadonlyArray<{ id: EquipmentPageCategory; one: string; many: string }> = [
  { id: 'poe-switch', one: 'PoE switch', many: 'PoE switches' },
  { id: 'mount', one: 'Mount', many: 'Mounts and junction boxes' },
  { id: 'ups', one: 'UPS', many: 'UPS' },
  { id: 'power-supply', one: 'Power supply', many: 'Power supplies' },
  { id: 'poe-injector', one: 'PoE injector', many: 'PoE injectors' },
  { id: 'media-converter', one: 'Media converter', many: 'Media converters' },
  { id: 'ai-box', one: 'AI box', many: 'AI boxes' },
  { id: 'ir-illuminator', one: 'IR illuminator', many: 'IR illuminators' },
  { id: 'ip-speaker', one: 'IP speaker', many: 'IP speakers' },
  { id: 'intercom', one: 'Intercom', many: 'Intercoms' },
  { id: 'access-reader', one: 'Access reader', many: 'Access readers' },
  { id: 'door-controller', one: 'Door controller', many: 'Door controllers' },
  { id: 'alarm-panel', one: 'Alarm panel', many: 'Alarm panels' },
  { id: 'keypad', one: 'Keypad', many: 'Keypads' },
  { id: 'indoor-ceiling-pir', one: 'Ceiling PIR detector', many: 'Ceiling PIR detectors' },
  { id: 'outdoor-wall-pir', one: 'Outdoor PIR detector', many: 'Outdoor PIR detectors' },
  { id: 'curtain-pir', one: 'Curtain PIR detector', many: 'Curtain PIR detectors' },
  { id: 'boundary-pir', one: 'Boundary PIR detector', many: 'Boundary PIR detectors' },
  { id: 'beam-barrier', one: 'Beam barrier', many: 'Beam barriers' },
  { id: 'fence-detector', one: 'Fence detector', many: 'Fence detectors' },
  { id: 'fall-detection-radar', one: 'Fall detection radar', many: 'Fall detection radars' },
  { id: 'magnetic-contact', one: 'Magnetic contact', many: 'Magnetic contacts' },
  { id: 'glass-break', one: 'Glass-break detector', many: 'Glass-break detectors' },
  { id: 'smoke-detector', one: 'Smoke detector', many: 'Smoke detectors' },
  { id: 'panic-button', one: 'Panic button', many: 'Panic buttons' },
  { id: 'outdoor-siren', one: 'Outdoor siren', many: 'Outdoor sirens' },
  { id: 'indoor-sounder', one: 'Indoor sounder', many: 'Indoor sounders' },
  { id: 'wireless-expander', one: 'Wireless expander', many: 'Wireless expanders' },
  { id: 'battery', one: 'Battery', many: 'Batteries' },
  { id: 'router', one: 'Router', many: 'Routers' },
  { id: 'wireless-access-point', one: 'Wireless access point', many: 'Wireless access points' },
  { id: 'radio-antenna', one: 'Radio link antenna', many: 'Radio link antennas' },
  { id: 'cellular-antenna', one: 'Cellular antenna', many: 'Cellular antennas' },
  { id: 'pole-enclosure', one: 'Pole enclosure', many: 'Pole enclosures' },
  { id: 'barrier-gate', one: 'Barrier gate', many: 'Barrier gates' },
  { id: 'solar-charge-controller', one: 'Solar charge controller', many: 'Solar charge controllers' },
  { id: 'workstation', one: 'Workstation', many: 'Workstations' },
];

export const MOUNT_KIND_LABEL: Record<MountKind, string> = {
  'pole-mount': 'Pole mount',
  'wall-mount': 'Wall mount',
  'corner-mount': 'Corner mount',
  'ceiling-mount': 'Ceiling mount',
  'junction-box': 'Junction box',
  'parapet-mount': 'Parapet mount',
  'plane-mount': 'Flat surface mount',
  'power-box': 'Power box',
  'adapter': 'Mount adapter',
};

const CATEGORY_IDS = new Set(EQUIPMENT_CATEGORIES.map(c => c.id));

export function categoryInfo(id: string) {
  return EQUIPMENT_CATEGORIES.find(c => c.id === id);
}

export function generateEquipmentSlug(manufacturer: string, model: string): string {
  return `${manufacturer}-${model}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Fields every catalog entry carries that are not figures of the device. */
const BASE_KEYS = new Set(['id', 'manufacturer', 'model', 'category', 'tagline', 'verified', 'datasheetUrl', 'hidden', 'custom', 'specs']);

let _all: EquipmentPageData[] | null = null;

/** Every publishable entry, deduplicated by category and slug, in catalog order. */
export function getAllPublishableEquipment(): EquipmentPageData[] {
  if (_all) return _all;
  const seen = new Set<string>();
  const out: EquipmentPageData[] = [];
  const add = (d: Omit<EquipmentPageData, 'slug' | 'path'>) => {
    const slug = generateEquipmentSlug(d.manufacturer, d.model);
    const key = `${d.category}/${slug}`;
    if (!slug || seen.has(key)) return;
    seen.add(key);
    out.push({ ...d, slug, path: `/equipment/${key}` });
  };
  for (const e of EQUIPMENT_CATALOG) {
    const any = e as unknown as Record<string, unknown>;
    if (e.category === 'nvr-recorder' || !CATEGORY_IDS.has(e.category)) continue;
    if (any.verified !== true || typeof any.datasheetUrl !== 'string' || !any.datasheetUrl || any.hidden || any.custom) continue;
    // Detection sensors keep their figures at the top level; the rest in `specs`.
    const specs = 'specs' in e && e.specs
      ? (e.specs as unknown as Record<string, unknown>)
      : Object.fromEntries(Object.entries(any).filter(([k]) => !BASE_KEYS.has(k)));
    add({ category: e.category, manufacturer: e.manufacturer, model: e.model, tagline: e.tagline, datasheetUrl: any.datasheetUrl, specs });
  }
  for (const m of [...MOUNT_ACCESSORIES, ...DAHUA_MOUNT_ACCESSORIES]) add(mountPageData(m));
  _all = out;
  return out;
}

function mountPageData(m: MountAccessory): Omit<EquipmentPageData, 'slug' | 'path'> {
  const { manufacturer, model, kind, datasheetUrl, ...rest } = m;
  return { category: 'mount', manufacturer, model, datasheetUrl, mountKind: kind, specs: rest as Record<string, unknown> };
}

/** "equipment/<category>/<slug>" for SSG and the sitemap. Skippable like the camera pages. */
export function getAllEquipmentSlugs(): string[] {
  if (typeof process !== 'undefined' && process.env?.SKIP_CAMERA_PAGES === '1') return [];
  return getAllPublishableEquipment().map(e => e.path.slice(1));
}

/** Category hubs that have at least one page, for SSG and the sitemap. */
export function getEquipmentCategoryPaths(): string[] {
  const used = new Set(getAllPublishableEquipment().map(e => e.category));
  return EQUIPMENT_CATEGORIES.filter(c => used.has(c.id)).map(c => `equipment/${c.id}`);
}

let _byPath: Map<string, EquipmentPageData> | null = null;
export function getEquipmentByPath(category: string, slug: string): EquipmentPageData | undefined {
  if (!_byPath) {
    _byPath = new Map();
    for (const e of getAllPublishableEquipment()) _byPath.set(`${e.category}/${e.slug}`, e);
  }
  return _byPath.get(`${category}/${slug}`);
}

export interface EquipmentBrandGroup { manufacturer: string; items: EquipmentPageData[] }

/** One category's pages grouped by maker, biggest first; deterministic order for byte-identical prerenders. */
export function getEquipmentByBrand(category: string): EquipmentBrandGroup[] {
  const by = new Map<string, EquipmentPageData[]>();
  for (const e of getAllPublishableEquipment()) {
    if (e.category !== category) continue;
    const list = by.get(e.manufacturer);
    if (list) list.push(e); else by.set(e.manufacturer, [e]);
  }
  return [...by.entries()]
    .map(([manufacturer, items]) => ({ manufacturer, items: [...items].sort((a, b) => a.model.localeCompare(b.model)) }))
    .sort((a, b) => b.items.length - a.items.length || a.manufacturer.localeCompare(b.manufacturer));
}

export function getCategoryCounts(): Array<{ id: EquipmentPageCategory; one: string; many: string; count: number }> {
  const n = new Map<string, number>();
  for (const e of getAllPublishableEquipment()) n.set(e.category, (n.get(e.category) ?? 0) + 1);
  return EQUIPMENT_CATEGORIES.filter(c => n.get(c.id)).map(c => ({ ...c, count: n.get(c.id)! }));
}

/** The figure that ranks siblings within a category (ports, VA, watts…); 0 where there is none. */
function sizeOf(e: EquipmentPageData): number {
  const s = e.specs;
  const num = (k: string) => (typeof s[k] === 'number' ? (s[k] as number) : 0);
  switch (e.category) {
    case 'poe-switch': return num('totalPorts');
    case 'ups': return num('capacityVA');
    case 'power-supply': return num('outputVoltageV') * num('outputA');
    default: return 0;
  }
}

/** Same maker, same category, nearest in size (ports, VA, watts). */
export function getRelatedEquipment(e: EquipmentPageData, max = 6): EquipmentPageData[] {
  const here = sizeOf(e);
  return getAllPublishableEquipment()
    .filter(o => o.category === e.category && o.manufacturer === e.manufacturer && o.slug !== e.slug && (e.category !== 'mount' || o.mountKind === e.mountKind))
    .sort((a, b) => Math.abs(sizeOf(a) - here) - Math.abs(sizeOf(b) - here) || a.model.localeCompare(b.model))
    .slice(0, max);
}

/** Other makers in the same category and size band, one per maker. */
export function getAlternativeEquipment(e: EquipmentPageData, max = 4): EquipmentPageData[] {
  const here = sizeOf(e);
  const picks: EquipmentPageData[] = [];
  const pool = getAllPublishableEquipment()
    .filter(o => o.category === e.category && o.manufacturer !== e.manufacturer && (e.category !== 'mount' || o.mountKind === e.mountKind))
    .sort((a, b) => Math.abs(sizeOf(a) - here) - Math.abs(sizeOf(b) - here) || a.model.localeCompare(b.model));
  for (const o of pool) {
    if (picks.length >= max) break;
    if (!picks.some(p => p.manufacturer === o.manufacturer)) picks.push(o);
  }
  return picks;
}

export interface CompatibleCamera { manufacturer: string; model: string; path?: string }

let _mountCameras: Map<string, CompatibleCamera[]> | null = null;

/**
 * The cameras whose maker lists this mount as one of their accessories
 * (tools/accessory-compatibility.json, read from the makers' camera pages),
 * one entry per camera model (lens versions folded), linked where the camera
 * has a page.
 */
export function getCamerasForMount(e: EquipmentPageData): CompatibleCamera[] {
  if (e.category !== 'mount') return [];
  if (!_mountCameras) {
    const pages = new Map<string, string>();
    for (const c of getAllCamerasForStaticPages()) pages.set(`${c.manufacturer}|${c.model}`, c.path);
    _mountCameras = new Map();
    for (const [brand, cams] of Object.entries(compatibility as Record<string, Record<string, string[]>>)) {
      for (const [camera, accs] of Object.entries(cams)) {
        const base = camera.replace(/\s*\(?\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*mm\)?.*$/i, '').trim() || camera;
        for (const acc of accs) {
          const list = _mountCameras.get(acc) ?? [];
          if (!list.some(c => c.manufacturer === brand && c.model === base)) {
            list.push({ manufacturer: brand, model: base, path: pages.get(`${brand}|${base}`) ?? pages.get(`${brand}|${camera}`) });
          }
          _mountCameras.set(acc, list);
        }
      }
    }
    for (const list of _mountCameras.values()) list.sort((a, b) => Number(!a.path) - Number(!b.path) || a.model.localeCompare(b.model));
  }
  return _mountCameras.get(e.model) ?? [];
}

/** Median maker-stated maximum draw of the 17,789 catalog cameras with one (tools/camera-power.json), W. */
export const TYPICAL_CAMERA_W = 9;

/** Human label and unit for each figure; anything not listed is shown with its key spelled out. */
const LABELS: Record<string, [string, string?]> = {
  totalPorts: ['Ports'], poePorts: ['PoE ports'], poeBudgetW: ['PoE budget', 'W'], poeStandard: ['PoE standard'],
  poePerPortW: ['Max power per PoE port', 'W'], uplinks: ['Uplinks'], switchingCapacityGbps: ['Switching capacity', 'Gbps'],
  management: ['Management'], formFactor: ['Form factor'], fanless: ['Fanless'], operatingTempC: ['Operating temperature', '°C'],
  surgeProtectionKV: ['Surge protection', 'kV'], powerConsumptionW: ['Own power draw', 'W'],
  capacityVA: ['Capacity', 'VA'], capacityW: ['Real power', 'W'], topology: ['Topology'], batteryType: ['Battery type'],
  outletsCount: ['Outlets'], outletType: ['Outlet types'], runtimeAtHalfLoadMin: ['Runtime at half load', 'min'],
  runtimeAtFullLoadMin: ['Runtime at full load', 'min'], networkManagement: ['Management interface'], weightKg: ['Weight', 'kg'],
  depthMm: ['Depth', 'mm'], outputVoltageV: ['Output voltage', 'V DC'], outputA: ['Output current', 'A'], outputW: ['Output power', 'W'],
  channels: ['Outputs'], perChannelA: ['Current per output', 'A'], batteryBacked: ['Battery backup'],
  batteryCapacityAh: ['Battery capacity', 'Ah'], inputVoltage: ['Input'], efficiencyPct: ['Efficiency', '%'],
  enclosureIncluded: ['Enclosure included'], ipRating: ['Ingress protection'], material: ['Material'], colour: ['Colour'],
  loadKg: ['Load rating', 'kg'], poleDiameterMm: ['Pole diameter', 'mm'], dimensionsMm: ['Dimensions', 'mm'],
  rangeM: ['Detection range', 'm'], hfovDeg: ['Detection angle', '°'], ports: ['Ports'],
};

const spell = (k: string) => k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

/** The entry's figures as label / value rows, in a stable order; empty and object values skipped. */
export function getSpecRows(e: EquipmentPageData): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  if (e.mountKind) rows.push({ label: 'Type', value: MOUNT_KIND_LABEL[e.mountKind] });
  for (const [k, v] of Object.entries(e.specs)) {
    if (v === undefined || v === null || v === '') continue;
    const [label, unit] = LABELS[k] ?? [spell(k)];
    let value: string;
    if (typeof v === 'boolean') value = v ? 'Yes' : 'No';
    else if (typeof v === 'number') value = `${v}${unit ? ` ${unit}` : ''}`;
    else if (typeof v === 'string') value = unit && /^-?\d/.test(v) && !v.includes(unit) ? `${v} ${unit}` : v;
    else if (Array.isArray(v) && v.every(x => typeof x === 'string' || typeof x === 'number')) {
      value = k === 'dimensionsMm' ? `${v.join(' × ')} mm` : k === 'poleDiameterMm' ? `${v[0]}–${v[1]} mm` : v.join(', ');
    } else continue;
    rows.push({ label, value });
  }
  return rows;
}

/**
 * One computed planning line per category, from the entry's own figures only
 * (and TYPICAL_CAMERA_W, stated as such): how many cameras a switch or UPS
 * carries. Undefined where the figures don't support one.
 */
export function getPlanningNote(e: EquipmentPageData): string | undefined {
  const s = e.specs as Record<string, number | string | undefined>;
  if (e.category === 'poe-switch' && typeof s.poeBudgetW === 'number' && typeof s.poePorts === 'number') {
    const byBudget = Math.floor(s.poeBudgetW / TYPICAL_CAMERA_W);
    const n = Math.min(s.poePorts, byBudget);
    return `At ${TYPICAL_CAMERA_W} W per camera (the median maximum draw makers state for the cameras in our catalog), the ${s.poeBudgetW} W budget powers ${byBudget} cameras, so all ${s.poePorts} PoE ports ${byBudget >= s.poePorts ? 'can be used at once' : `cannot: plan for ${n}`}.`;
  }
  if (e.category === 'ups' && typeof s.capacityW === 'number') {
    const n = Math.floor(s.capacityW / TYPICAL_CAMERA_W);
    const rt = typeof s.runtimeAtHalfLoadMin === 'number' ? ` At half load (${Math.round(s.capacityW / 2)} W) the maker states ${s.runtimeAtHalfLoadMin} minutes of runtime.` : '';
    return `${s.capacityW} W of real power is the draw of about ${n} PoE cameras at ${TYPICAL_CAMERA_W} W each, before the recorder and switch.${rt}`;
  }
  if (e.category === 'power-supply' && typeof s.outputVoltageV === 'number' && typeof s.outputA === 'number' && s.outputVoltageV >= 11 && s.outputVoltageV <= 14) {
    const n = Math.floor((s.outputVoltageV * s.outputA) / TYPICAL_CAMERA_W);
    return `${s.outputVoltageV} V × ${s.outputA} A is about ${Math.round(s.outputVoltageV * s.outputA)} W: enough for ${n} cameras powered at 12 V, at ${TYPICAL_CAMERA_W} W each.`;
  }
  return undefined;
}
